"use client";

import { useEffect, useRef, useState } from "react";
import { getChatMessages, type ChatMessage } from "@/lib/api";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

export type RealtimeStatus = "connecting" | "live" | "polling" | "offline";

interface Options {
  /** false 면 연결/폴링 전부 정지. 로그인 상태 전환 시 토글. */
  enabled?: boolean;
  /** 새 메시지 단건 도착 */
  onMessage: (msg: ChatMessage) => void;
  /** 일괄 catch-up 결과 */
  onBatch?: (msgs: ChatMessage[]) => void;
  /** 현재까지 수신한 최대 message id (since_id 로 사용) */
  getSinceId: () => number;
}

function httpToWs(url: string): string {
  return url.replace(/^http:/i, "ws:").replace(/^https:/i, "wss:");
}

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("access_token");
}

/**
 * 팀 채팅 실시간 수신 — WebSocket 우선, 3회 실패 시 HTTP 폴링 fallback.
 *
 * - 재연결 backoff: 1s → 2s → 4s → ... cap 30s
 * - 3회 연속 실패하면 polling 모드로 전환. 폴링 중에도 주기적으로 WS 재시도.
 * - 탭이 hidden 이면 타이머/소켓을 일시 중단, visible 복귀 시 catch-up + 재개.
 * - 폴링 주기 적응형: 최근 메시지 이후 경과 시간 기준 1s/3s/5s/10s.
 */
export function useChatRealtime(
  entityType: string,
  entityId: string,
  { enabled = true, onMessage, onBatch, getSinceId }: Options,
) {
  const [status, setStatus] = useState<RealtimeStatus>(
    enabled ? "connecting" : "offline",
  );

  const wsRef = useRef<WebSocket | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wsRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const failCountRef = useRef<number>(0);
  const retryDelayRef = useRef<number>(1000);
  const pollingActiveRef = useRef<boolean>(false);
  const lastMessageAtRef = useRef<number>(Date.now());
  const stoppedRef = useRef<boolean>(false);
  const catchUpInFlightRef = useRef<boolean>(false);

  // 콜백은 ref 로 최신값 유지 (재연결 시 stale closure 방지)
  const onMessageRef = useRef(onMessage);
  const onBatchRef = useRef(onBatch);
  const getSinceIdRef = useRef(getSinceId);
  useEffect(() => {
    onMessageRef.current = onMessage;
    onBatchRef.current = onBatch;
    getSinceIdRef.current = getSinceId;
  }, [onMessage, onBatch, getSinceId]);

  useEffect(() => {
    if (!enabled) {
      setStatus("offline");
      return;
    }
    stoppedRef.current = false;

    function clearPing() {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    }
    function clearReconnect() {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }
    function clearPoll() {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
    function clearWsRetry() {
      if (wsRetryTimerRef.current) {
        clearTimeout(wsRetryTimerRef.current);
        wsRetryTimerRef.current = null;
      }
    }

    async function catchUp() {
      // 중복 polling/visibilitychange/onOnline 등에서 동시 호출돼도
      // 단일 요청만 진행 — 응답 race 로 stale 한 값을 덮어쓰지 않게 한다.
      if (catchUpInFlightRef.current) return;
      catchUpInFlightRef.current = true;
      const sinceId = getSinceIdRef.current();
      try {
        const res = await getChatMessages(entityType, entityId, {
          since_id: sinceId,
        });
        if (res.messages.length > 0) {
          lastMessageAtRef.current = Date.now();
          if (onBatchRef.current) {
            onBatchRef.current(res.messages);
          } else {
            res.messages.forEach((m) => onMessageRef.current(m));
          }
        }
      } catch {
        /* ignore */
      } finally {
        catchUpInFlightRef.current = false;
      }
    }

    function pickPollDelay(): number {
      const quietMs = Date.now() - lastMessageAtRef.current;
      if (quietMs < 60_000) return 1000; // 최근 1분 → 1초
      if (quietMs < 5 * 60_000) return 3000; // 5분 이내 → 3초
      if (quietMs < 15 * 60_000) return 5000; // 15분 이내 → 5초
      return 10_000; // 그 이후 → 10초
    }

    async function pollOnce() {
      await catchUp();
      if (stoppedRef.current || !pollingActiveRef.current) return;
      if (document.visibilityState === "hidden") return;
      pollTimerRef.current = setTimeout(pollOnce, pickPollDelay());
    }

    function startPolling() {
      if (pollingActiveRef.current) return;
      pollingActiveRef.current = true;
      setStatus("polling");
      pollOnce();
      // polling 중에도 주기적으로 WS 재시도 (30초)
      if (!wsRetryTimerRef.current) {
        const retry = () => {
          if (stoppedRef.current) return;
          if (!pollingActiveRef.current) return;
          connectWs();
          wsRetryTimerRef.current = setTimeout(retry, 30_000);
        };
        wsRetryTimerRef.current = setTimeout(retry, 30_000);
      }
    }

    function stopPolling() {
      pollingActiveRef.current = false;
      clearPoll();
    }

    function connectWs() {
      if (stoppedRef.current) return;

      const token = getToken();
      if (!token) {
        setStatus("offline");
        return;
      }

      setStatus((s) => (s === "polling" ? s : "connecting"));

      const url = `${httpToWs(API_URL)}/ws/chat/${encodeURIComponent(
        entityType,
      )}/${encodeURIComponent(entityId)}?token=${encodeURIComponent(token)}`;

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        handleFailure();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        failCountRef.current = 0;
        retryDelayRef.current = 1000;
        setStatus("live");
        // WS 전환 성공 → polling 중단
        stopPolling();
        clearWsRetry();
        // 끊어진 동안 누락된 메시지 catch-up
        void catchUp();
        // keepalive ping
        clearPing();
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send("ping");
            } catch {
              /* ignore */
            }
          }
        }, 30_000);
      };

      ws.onmessage = (ev) => {
        if (ev.data === "pong") return;
        try {
          const payload = JSON.parse(ev.data);
          if (payload?.type === "message.new" && payload.message) {
            lastMessageAtRef.current = Date.now();
            onMessageRef.current(payload.message as ChatMessage);
          }
        } catch {
          /* ignore non-JSON */
        }
      };

      ws.onerror = () => {
        /* close 에서 처리 */
      };

      ws.onclose = () => {
        clearPing();
        if (wsRef.current === ws) wsRef.current = null;
        if (stoppedRef.current) return;
        handleFailure();
      };
    }

    function handleFailure() {
      failCountRef.current += 1;

      // 3회 연속 실패 → 폴링으로 전환
      if (failCountRef.current >= 3 && !pollingActiveRef.current) {
        startPolling();
      }

      // WS 재연결 backoff (폴링 중이어도 background 재시도 병행)
      const delay = retryDelayRef.current;
      retryDelayRef.current = Math.min(retryDelayRef.current * 2, 30_000);
      clearReconnect();
      if (!pollingActiveRef.current) {
        reconnectTimerRef.current = setTimeout(connectWs, delay);
      }
    }

    function onVisibility() {
      if (document.visibilityState === "hidden") {
        // 절전 — ping/polling 멈춤 (소켓은 브라우저가 유지)
        clearPing();
        clearPoll();
      } else {
        // 복귀 — catch-up 후 상태 재개
        void catchUp();
        if (pollingActiveRef.current) {
          pollOnce();
        } else if (!wsRef.current || wsRef.current.readyState > 1) {
          connectWs();
        } else if (!pingTimerRef.current) {
          pingTimerRef.current = setInterval(() => {
            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) {
              try {
                ws.send("ping");
              } catch {
                /* ignore */
              }
            }
          }, 30_000);
        }
      }
    }

    function onOnline() {
      void catchUp();
      if (!wsRef.current || wsRef.current.readyState > 1) {
        connectWs();
      }
    }

    connectWs();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);

    return () => {
      stoppedRef.current = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      clearPing();
      clearReconnect();
      clearPoll();
      clearWsRetry();
      pollingActiveRef.current = false;
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
    };
  }, [entityType, entityId, enabled]);

  return { status };
}
